
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { TrendingDown, ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { BallTendency } from "@/hooks/level3/types";

interface IronScoreButtonsProps {
  onScoreChange: (score: number, tendency?: BallTendency) => void;
}

const IronScoreButtons: React.FC<IronScoreButtonsProps> = ({ onScoreChange }) => {
  const { t } = useTranslation('training');
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [showTendency, setShowTendency] = useState(false);

  const handleScoreClick = (score: number) => {
    setSelectedScore(score);
    
    if (score > 0) {
      setShowTendency(true);
    } else {
      onScoreChange(score, null);
      setSelectedScore(null);
    }
  };

  const handleTendencyClick = (tendency: BallTendency) => {
    if (selectedScore !== null) {
      onScoreChange(selectedScore, tendency);
      setSelectedScore(null);
      setShowTendency(false);
    }
  };

  const resetSelection = () => {
    setSelectedScore(null);
    setShowTendency(false);
  };

  if (showTendency) {
    return (
      <div className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-md border border-blue-100 text-center mb-2">
          <p className="text-blue-800 font-medium">{t('level1.scoring.iron.selectTendency')}</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Button 
            onClick={() => handleTendencyClick('left')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <ArrowLeft className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('level1.scoring.iron.left')}</span>
          </Button>
          <Button 
            onClick={() => handleTendencyClick('center')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <TrendingDown className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('level1.scoring.iron.center')}</span>
          </Button>
          <Button 
            onClick={() => handleTendencyClick('right')}
            className="h-12 bg-gray-100 hover:bg-gray-200 text-gray-800 flex flex-col items-center"
          >
            <ArrowRight className="h-5 w-5 mb-1" />
            <span className="text-xs">{t('level1.scoring.iron.right')}</span>
          </Button>
        </div>
        <Button 
          onClick={resetSelection}
          className="h-10 w-full text-sm bg-gray-200 hover:bg-gray-300 text-gray-700"
        >
          {t('level1.scoring.iron.cancel')}
        </Button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <Button 
        className="h-14 bg-green-500 hover:bg-green-600 text-white border-0 text-base font-medium" 
        onClick={() => handleScoreClick(10)}
      >
        {t('level1.scoring.iron.ballIn')} (10 pts)
      </Button>
      <Button 
        className="h-14 bg-green-400 hover:bg-green-500 text-white border-0 text-base font-medium" 
        onClick={() => handleScoreClick(8)}
      >
        {t('level1.scoring.iron.at1m')} (8 pts)
      </Button>
      <Button 
        className="h-14 bg-blue-500 hover:bg-blue-600 text-white border-0 text-base font-medium" 
        onClick={() => handleScoreClick(6)}
      >
        {t('level1.scoring.iron.at3m')} (6 pts)
      </Button>
      <Button 
        className="h-14 bg-yellow-500 hover:bg-yellow-600 text-white border-0 text-base font-medium" 
        onClick={() => handleScoreClick(3)}
      >
        {t('level1.scoring.iron.at5m')} (3 pts)
      </Button>
      <Button 
        className="h-14 bg-red-500 hover:bg-red-600 text-white border-0 text-base font-medium" 
        onClick={() => handleScoreClick(0)}
      >
        {t('level1.scoring.iron.outsideCircles')} (0 pt)
      </Button>
    </div>
  );
};

export default IronScoreButtons;
